/*@jsxRuntime automatic @jsxImportSource react*/
import {jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {MainLayout} from '~/layouts/main';
import {Figure} from '~/components/Figure';
import {Quiz} from '~/components/Quiz';
export const meta = {
  title: 'SRP | Введение',
  description: 'Введение в принцип единственной ответственности, его смысл, основные понятия'
};
const MDXLayout = ({children}) => _jsx(MainLayout, {
  meta: meta,
  children: children
});
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      section: "section",
      h1: "h1",
      p: "p",
      em: "em",
      h2: "h2",
      a: "a",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_components.section, {
      children: [_jsx(_components.h1, {
        children: "Введение"
      }), _jsx(_components.p, {
        children: "Чтобы вникнуть в этот и другие принципы, нам понадобится ввести несколько понятий, которые будем использовать."
      }), _jsxs(_components.p, {
        children: [_jsx(_components.em, {
          children: "Модулем"
        }), " мы будем называть какую-то часть кода, обособленную от других. Это может быть класс, функция, объект, файл — в общем, что-то, у чего есть границы, отделяющие этот код от другого."]
      }), _jsxs(_components.p, {
        children: [_jsx(_components.em, {
          children: "Бизнес-правилами"
        }), " будем называть правила взаимодействия сущностей друг с другом и внешней средой. Под внешней средой будем понимать всё, что влияет на программу извне — пользовательский ввод, события, вызов API и т.д."]
      }), _jsxs(_components.p, {
        children: [_jsx(_components.em, {
          children: "Причиной изменения"
        }), " будем называть обновление бизнес-правил, которое вынуждает менять код какого-либо модуля."]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Принцип единственной ответственности"
        }), _jsxs(_components.p, {
          children: [_jsx(_components.a, {
            href: "https://ru.wikipedia.org/wiki/%D0%9F%D1%80%D0%B8%D0%BD%D1%86%D0%B8%D0%BF_%D0%B5%D0%B4%D0%B8%D0%BD%D1%81%D1%82%D0%B2%D0%B5%D0%BD%D0%BD%D0%BE%D0%B9_%D0%BE%D1%82%D0%B2%D0%B5%D1%82%D1%81%D1%82%D0%B2%D0%B5%D0%BD%D0%BD%D0%BE%D1%81%D1%82%D0%B8",
            children: "Принцип единственной ответственности"
          }), " (Single Responsibility Principle, SRP) означает, что у модуля должна быть только одна причина для изменения. Весь код, который меняется по этой причине, должен быть собран в этом модуле."]
        }), _jsx(_components.p, {
          children: "Проще говоря принцип предлагает нам проводить границы между модулями так, чтобы изменение в бизнес-правилах затрагивало как можно меньше модулей, в идеале — один."
        }), _jsx(_components.p, {
          children: "Основной инструмент принципа — объединять те части, которые меняются по одной причине, и разделять те, которые меняются по разным."
        }), _jsxs(Figure, {
          children: [_jsx("img", {
            src: "/srp.svg"
          }), _jsx("figcaption", {
            children: "Смысл принципа — в объединении частей, которые меняются по одной причине, и разделении тех, которые меняются по разным"
          })]
        }), _jsx(_components.p, {
          children: "Принцип позволяет уменьшить количество кода, который нужно менять при изменении бизнес-правил. Он помогает ограничить влияние этих изменений и контролировать сложность программы."
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Коротко"
        }), _jsx(_components.p, {
          children: "Принцип единственной ответственности:"
        }), _jsxs(_components.ul, {
          children: ["\n", _jsx(_components.li, {
            children: "помогает разбивать и декомпозировать задачи по одной на модуль;"
          }), "\n", _jsx(_components.li, {
            children: "уменьшает количество модулей, которые надо изменить при изменении требований;"
          }), "\n", _jsx(_components.li, {
            children: "ограничивает влияние изменений, помогая контролировать сложность системы."
          }), "\n"]
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Материалы к разделу"
        }), _jsxs(_components.ul, {
          children: ["\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://hackernoon.com/you-dont-understand-the-single-responsibility-principle-abfdd005b137",
              children: "Think you understand SRP?"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://habr.com/ru/post/454290/",
              children: "SRP: Не такой простой, как кажется"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://ru.wikipedia.org/wiki/%D0%9F%D1%80%D0%B8%D0%BD%D1%86%D0%B8%D0%BF_%D0%B5%D0%B4%D0%B8%D0%BD%D1%81%D1%82%D0%B2%D0%B5%D0%BD%D0%BD%D0%BE%D0%B9_%D0%BE%D1%82%D0%B2%D0%B5%D1%82%D1%81%D1%82%D0%B2%D0%B5%D0%BD%D0%BD%D0%BE%D1%81%D1%82%D0%B8",
              children: "Принцип единственной ответственности"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://code.tutsplus.com/ru/tutorials/solid-part-1-the-single-responsibility-principle--net-36074",
              children: "SOLID: Часть 1, SRP"
            })
          }), "\n"]
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Вопросы"
        }), _jsx(Quiz, {
          name: "srp-intro-1"
        }), _jsx(Quiz, {
          name: "srp-intro-2"
        })]
      })]
    });
  }
}
export default MDXContent;
